import { useEffect, type RefObject } from 'react';
import { chatBotManager } from '@/services/chatBotManager';

const MIN_CTA_HEIGHT_PX = 40;
const OPEN_SHEET = '.rg-help-sheet.show, .rg-modal-back.show';

const ctaTopFromBottom = (screen: HTMLElement) => {
  // innerHeight follows the mobile toolbar like the fixed launcher does; clientHeight does not.
  const viewport = window.innerHeight;
  for (const cta of screen.querySelectorAll('.rg-foot button')) {
    const { top, bottom, height } = cta.getBoundingClientRect();
    if (height >= MIN_CTA_HEIGHT_PX && bottom > 0 && top < viewport) {
      return viewport - top;
    }
  }
  return null;
};

export const useChatLauncherDock = (screenRef: RefObject<HTMLElement>, active: boolean) => {
  useEffect(() => {
    const screen = screenRef.current;
    if (!active || !screen) {
      return undefined;
    }
    const update = () => {
      chatBotManager.suppressLauncher(screen.querySelector(OPEN_SHEET) !== null);
      chatBotManager.placeLauncherAbove(ctaTopFromBottom(screen));
    };
    // Scroll, resize and observer callbacks can land several times per frame; measure once.
    let frame = 0;
    const scheduleUpdate = () => {
      if (frame) {
        return;
      }
      frame = requestAnimationFrame(() => {
        frame = 0;
        update();
      });
    };
    const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(scheduleUpdate);
    const observeFeet = () => {
      resize?.disconnect();
      resize?.observe(screen);
      screen.querySelectorAll('.rg-foot').forEach((foot) => resize?.observe(foot));
    };
    // Class flips (sheets, validation) only move things; only added/removed nodes can change the feet.
    const mutations = new MutationObserver((records) => {
      if (records.some((record) => record.type === 'childList')) {
        observeFeet();
      }
      scheduleUpdate();
    });
    mutations.observe(screen, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
    screen.addEventListener('animationend', scheduleUpdate);
    screen.addEventListener('transitionend', scheduleUpdate);
    window.addEventListener('resize', scheduleUpdate);
    // The page footer sits below the 100dvh phone screen, so the window does scroll.
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    observeFeet();
    update();
    return () => {
      cancelAnimationFrame(frame);
      mutations.disconnect();
      resize?.disconnect();
      screen.removeEventListener('animationend', scheduleUpdate);
      screen.removeEventListener('transitionend', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      window.removeEventListener('scroll', scheduleUpdate);
      chatBotManager.suppressLauncher(false);
      chatBotManager.placeLauncherAbove(null);
    };
  }, [screenRef, active]);
};
